import React, { useState } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import ButtonDark from './ui/button/buttonDark';
import Toast from './ui/toast/toast';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showToast, setShowToast] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // German phone number format (flexible)
    const phoneRegex = /^[\d\s\/\-\+\(\)]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 6;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Bitte geben Sie Ihren Namen ein';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Bitte geben Sie Ihre Telefonnummer ein';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Bitte geben Sie eine gültige Telefonnummer ein';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Bitte geben Sie eine Nachricht ein';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Die Nachricht muss mindestens 10 Zeichen lang sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        ...formData,
        tenantId: 'sinn'
      });

      const response = await fetch('https://supabase.grundworks.de/functions/v1/webflow-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Senden der Nachricht');
      }

      setSubmitStatus('success');
      setShowToast(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toast
        message="Deine Nachricht wurde erfolgreich gesendet! Wir melden uns bald bei dir."
        type="success"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
      <div className="w-full max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name Field */}
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-semibold text-slate-900 mb-2"
          >
            Name <span className="text-[#D06C60]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border ${
              errors.name
                ? 'border-red-300 bg-red-50'
                : 'border-slate-200 bg-white'
            } focus:outline-none focus:ring-2 focus:ring-[#D06C60]/20 focus:border-[#D06C60] transition-colors`}
            placeholder="Dein vollständiger Name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.name}
            </p>
          )}
        </div>

        <input type="hidden" name="tenantId" value="sinn" />
        {/* Email Field */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-semibold text-slate-900 mb-2"
          >
            E-Mail <span className="text-[#D06C60]">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border ${
              errors.email
                ? 'border-red-300 bg-red-50'
                : 'border-slate-200 bg-white'
            } focus:outline-none focus:ring-2 focus:ring-[#D06C60]/20 focus:border-[#D06C60] transition-colors`}
            placeholder="deine.email@beispiel.de"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <label
            htmlFor="phone"
            className="block text-sm font-semibold text-slate-900 mb-2"
          >
            Telefon <span className="text-[#D06C60]">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border ${
              errors.phone
                ? 'border-red-300 bg-red-50'
                : 'border-slate-200 bg-white'
            } focus:outline-none focus:ring-2 focus:ring-[#D06C60]/20 focus:border-[#D06C60] transition-colors`}
            placeholder="03695 / 123 456"
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.phone}
            </p>
          )}
        </div>

        {/* Message Field */}
        <div>
          <label
            htmlFor="message"
            className="block text-sm font-semibold text-slate-900 mb-2"
          >
            Nachricht <span className="text-[#D06C60]">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={6}
            className={`w-full px-4 py-3 rounded-xl border ${
              errors.message
                ? 'border-red-300 bg-red-50'
                : 'border-slate-200 bg-white'
            } focus:outline-none focus:ring-2 focus:ring-[#D06C60]/20 focus:border-[#D06C60] transition-colors resize-none`}
            placeholder="Beschreibe kurz, wofür du einen Termin benötigst oder stelle deine Frage..."
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex items-center gap-4">
          <ButtonDark
            onClick={() => handleSubmit(new Event('submit') as unknown as React.FormEvent)}
            disabled={isSubmitting}
            className="whitespace-nowrap"
          >Nachricht senden</ButtonDark>

          {/* Error Message */}
          {submitStatus === 'error' && (
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                Fehler beim Senden. Bitte versuche es erneut.
              </span>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-500">
          Mit dem Absenden des Formulars erklärst du dich mit der Verarbeitung
          deiner Daten einverstanden. Weitere Informationen findest du in unserer{' '}
          <a href="#" className="text-[#D06C60] hover:underline">
            Datenschutzerklärung
          </a>
          .
        </p>
      </form>
    </div>
    </>
  );
}

